import React, { useMemo, useState, useCallback, useEffect } from "react";
import { AccordionItem } from "./Accordion";
import { formatDate, statusDotClass, parseDate } from "./utils";
import { X } from "lucide-react";

function Field({ label, children }) {
  return (
    <div className="bi-field">
      <div className="bi-fieldLabel">{label}</div>
      <div className="bi-fieldVal">{children}</div>
    </div>
  );
}

function Grid2({ children }) {
  return <div className="bi-grid2">{children}</div>;
}

function ImplBadge({ value }) {
  const yes = String(value || "").toLowerCase() === "yes";
  return (
    <span className={`bi-badge ${yes ? "is-yes" : "is-no"}`}>
      {yes ? "Implemented" : "Not Implemented"}
    </span>
  );
}

function Timeline({ rows }) {
  if (!rows?.length) return <div className="bi-muted">—</div>;

  const items = rows
    .map(([label, raw]) => ({ label, raw, d: parseDate(raw) }))
    .filter((x) => x.raw)
    .sort((a, b) => {
      const ta = a.d?.getTime() || 0;
      const tb = b.d?.getTime() || 0;
      return ta - tb;
    });

  const lastIdx = items.length - 1;

  return (
    <div className="bi-timeline">
      {items.map((it, idx) => (
        <div key={it.label} className="bi-tlRow">
          <div className="bi-tlLeft">
            <span className={`bi-tlDot ${idx === lastIdx ? "is-active" : ""}`} />
            {idx !== lastIdx ? <span className="bi-tlLine" /> : null}
          </div>

          <div className="bi-tlLabel">{it.label.replace(" in Progress", "")}</div>

          <div className="bi-tlDate">{formatDate(it.raw)}</div>
        </div>
      ))}
    </div>
  );
}

export default function DetailsDrawer({ open, uiDetails, idea, onClose }) {
  const [openSet, setOpenSet] = useState(new Set(["Overview"]));

  const sections = uiDetails?.sections || [
    "Overview",
    "Implementation Details",
    "Submitter Details",
    "Status Timeline",
    "Submission Metadata",
  ];

  const toggle = (k) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const timeline = useMemo(() => {
    if (!idea) return [];
    const rows = [
      ["Submitted", idea["Submitted"]],
      ["Review", idea["Review in Progress"]],
      ["Approved - Pending Sponsor Approval", idea["Approved - Pending Sponsor Approval"]],
      ["Approved - Pending Implementation", idea["Approved - Pending Implementation"]],
      ["Development", idea["Development in Progress"]],
      ["Completed", idea["Completed"]],
      ["Denied", idea["Denied"]],
    ];
    return rows.filter((r) => r[1]);
  }, [idea]);

  // Supports Vite / Next / CRA / fallback
  const TSD_API_BASE = process.env.TSD_API_BASE || "http://localhost:8000";

  const [bcLoading, setBcLoading] = useState(false);
  const [bcProgress, setBcProgress] = useState("");
  const [bcReady, setBcReady] = useState(null);

  // Reset generated state when switching to another idea (fix: button stays "Download")
  useEffect(() => {
    setBcLoading(false);
    setBcProgress("");
    setBcReady((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
  }, [idea?.ID]);

  // Cleanup blob URL on unmount/change
  useEffect(() => {
    return () => {
      if (bcReady?.url) URL.revokeObjectURL(bcReady.url);
    };
  }, [bcReady]);

  const handleDownloadBusinessCase = useCallback(() => {
    if (!bcReady) return;
    const link = document.createElement("a");
    link.href = bcReady.url;
    link.download = bcReady.fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, [bcReady]);

  const handleGenerateBusinessCase = useCallback(async () => {
    if (bcLoading || !idea) return;

    setBcLoading(true);
    setBcReady(null);
    setBcProgress("Preparing data…");

    try {
      const payload = {
        idea: {
          ID: idea.ID,
          Title: idea.Title,
          Description: idea.Description,
          "Challenge/Opportunity": idea["Challenge/Opportunity"],
          "Proposed Solution": idea["Proposed Solution"],
          "Innovation Benefits": idea["Innovation Benefits"],
          "Current and Future Process Overview": idea["Current and Future Process Overview"],
          "Savings after Implementation": idea["Savings after Implementation"],

          Category: idea.Category,

          "Is implemented?": idea["Is implemented?"],
          "Implementation date": idea["Implementation date"],
          "Effort to Implement": idea["Effort to Implement"],
          "Cost to Implement": idea["Cost to Implement"],
          Attachment: idea.Attachment,

          "Submitter name": idea["Submitter name"],
          "Submitter email": idea["Submitter email"],
          "Co innovator name": idea["Co innovator name"],
          "Co innovator email": idea["Co innovator email"],
          Department: idea.Department,
          Demographic: idea.Demographic,
          "Manager name": idea["Manager name"],
          "Director name": idea["Director name"],

          Status: idea._statusKey,
          "Status Last modified date": idea["Status Last modified date"],
        },
      };

      setBcProgress("Generating Business Case…");

      const res = await fetch(`${TSD_API_BASE}/api/generate-business-case`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Server responded with ${res.status}`);
      }

      const blob = await res.blob();

      const disposition = res.headers.get("content-disposition") || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const fileName = match ? match[1] : `Business_Case_${idea.ID || "Idea"}.pptx`;

      const url = URL.createObjectURL(blob);
      setBcReady({ url, fileName });
    } catch (e) {
      alert(`Business case generation failed: ${e.message || e}`);
    } finally {
      setBcLoading(false);
      setBcProgress("");
    }
  }, [bcLoading, idea, TSD_API_BASE]);

  return (
    <div className={`bi-drawerWrap ${open ? "is-open" : ""}`} aria-hidden={!open}>
      <div className="bi-drawerBackdrop" onClick={onClose} />
      <aside className="bi-drawer">
        <div className="bi-drawerHead">
          <button type="button" className="bi-dClose" onClick={onClose} aria-label="Close">
            <X className="bi-dCloseIcon" aria-hidden="true" />
          </button>
        </div>

        {idea ? (
          <div className="bi-drawerBody">
            <div className="bi-dTitle">
              {idea.ID}: {idea.Title}
            </div>

            <div className="bi-dGrid">
              <div>
                <div className="bi-kLabel">Status</div>
                <div className="bi-status" style={{ marginTop: 8 }}>
                  <span className={statusDotClass(idea._statusKey)} />
                  {idea._statusKey}
                </div>

                <div style={{ marginTop: 18 }}>
                  <div className="bi-kLabel">Last updated</div>
                  <div className="bi-kVal">{formatDate(idea["Status Last modified date"])}</div>
                </div>
              </div>

              <div>
                <div className="bi-kLabel">Category</div>
                <div style={{ marginTop: 8 }}>
                  <span className="bi-pill is-cat">{idea.Category || "—"}</span>
                </div>
              </div>
            </div>

            <div className="bi-accordion">
              <AccordionItem
                title={sections[0]}
                open={openSet.has(sections[0])}
                onToggle={() => toggle(sections[0])}
              >
                <div className="bi-section">
                  <div className="bi-subhead">Description</div>
                  <div className="bi-par">{idea.Description || "—"}</div>

                  <div className="bi-subhead">Challenge / Opportunity</div>
                  <div className="bi-par">{idea["Challenge/Opportunity"] || "—"}</div>

                  <div className="bi-subhead">Proposed Solution</div>
                  <div className="bi-par">{idea["Proposed Solution"] || "—"}</div>

                  <div className="bi-subhead">Innovation Benefits</div>
                  <div className="bi-par">{idea["Innovation Benefits"] || "—"}</div>

                  <div className="bi-subhead">Current and Future Process</div>
                  <div className="bi-par">{idea["Current and Future Process Overview"] || "—"}</div>

                  <div className="bi-subhead">Savings after Implementation</div>
                  <div className="bi-par">{idea["Savings after Implementation"] || "—"}</div>
                </div>
              </AccordionItem>

              <AccordionItem
                title={sections[1]}
                open={openSet.has(sections[1])}
                onToggle={() => toggle(sections[1])}
              >
                <div className="bi-sectionCard">
                  <div className="bi-cardTitle">{sections[1]}</div>

                  <div className="bi-implTop">
                    <div>
                      <div className="bi-miniLabel">Implementation Status</div>
                      <div style={{ marginTop: 6 }}>
                        <ImplBadge value={idea["Is implemented?"]} />
                      </div>
                    </div>

                    <div>
                      <div className="bi-miniLabel">Implementation Date</div>
                      <div className="bi-miniVal">{formatDate(idea["Implementation date"])}</div>
                    </div>
                  </div>

                  <div className="bi-divider" />

                  <div className="bi-subhead">Effort to Implement</div>
                  <div className="bi-par">{String(idea["Effort to Implement"] || "—")}</div>

                  <div className="bi-subhead">Cost to Implement</div>
                  <div className="bi-par">{String(idea["Cost to Implement"] || "—")}</div>

                  <div className="bi-subhead">Attachment</div>
                  <div className="bi-par">{String(idea.Attachment || "—")}</div>
                </div>
              </AccordionItem>

              <AccordionItem
                title={sections[2]}
                open={openSet.has(sections[2])}
                onToggle={() => toggle(sections[2])}
              >
                <div className="bi-sectionCard">
                  <div className="bi-cardTitle">{sections[2]}</div>

                  <Grid2>
                    <Field label="Submitter Name">{String(idea["Submitter name"] || "—")}</Field>
                    <Field label="Submitter Email">{String(idea["Submitter email"] || "—")}</Field>

                    <Field label="Co-Innovator Name">
                      {String(idea["Co innovator name"] || "—")}
                    </Field>
                    <Field label="Co-Innovator Email">
                      {String(idea["Co innovator email"] || "—")}
                    </Field>

                    <Field label="Department">
                      {idea.Department ? (
                        <span className="bi-pill is-dept">{idea.Department}</span>
                      ) : (
                        "—"
                      )}
                    </Field>
                    <Field label="Demographic">
                      {idea.Demographic ? (
                        <span className="bi-pill is-dept">{idea.Demographic}</span>
                      ) : (
                        "—"
                      )}
                    </Field>

                    <Field label="Manager">{String(idea["Manager name"] || "—")}</Field>
                    <Field label="Director">{String(idea["Director name"] || "—")}</Field>
                  </Grid2>
                </div>
              </AccordionItem>

              <AccordionItem
                title={sections[3]}
                open={openSet.has(sections[3])}
                onToggle={() => toggle(sections[3])}
              >
                <div className="bi-sectionCard">
                  <div className="bi-cardTitle">{sections[3]}</div>
                  <Timeline rows={timeline} />
                </div>
              </AccordionItem>

              <AccordionItem
                title={sections[4]}
                open={openSet.has(sections[4])}
                onToggle={() => toggle(sections[4])}
              >
                <div className="bi-sectionCard">
                  <div className="bi-cardTitle">{sections[4]}</div>

                  <Grid2>
                    <Field label="Visits">{String(idea._visits)}</Field>
                    <Field label="Likes">{String(idea._likes)}</Field>

                    <Field label="Submitted Date">{formatDate(idea._submittedAt)}</Field>
                    <Field label="Status Last Modified">
                      {formatDate(idea["Status Last modified date"])}
                    </Field>
                  </Grid2>
                </div>
              </AccordionItem>
            </div>

            <div className="bi-drawerFooter">
              {bcReady ? (
                <button
                  type="button"
                  className="bi-btn bi-btn--cgi"
                  onClick={handleDownloadBusinessCase}
                  title="Download Business Case"
                >
                  Download Business Case
                </button>
              ) : (
                <button
                  type="button"
                  className="bi-btn bi-btn--cgi"
                  onClick={handleGenerateBusinessCase}
                  disabled={bcLoading || !idea}
                  title={bcLoading ? bcProgress : "Generate Business Case"}
                >
                  {bcLoading ? bcProgress || "Generating…" : "Generate Business Case"}
                </button>
              )}
            </div>
          </div>
        ) : null}
      </aside>
    </div>
  );
}
